import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { useWishlist } from "../context/WishlistContext";
import { useCart } from "../context/CartContext";
import { useShopMode } from "../context/ShopModeContext";
import { api, waLink } from "../lib/api";
import { AuthModal } from "./AuthModal";
import { BrandLogo } from "./BrandLogo";
import { BrandTmBadge } from "./BrandTmBadge";
import {
  getPendingAuthSession,
  getAuthModalActive,
} from "../lib/authSession";
import { toast } from "sonner";
import {
  Search, Menu, X, ShoppingBag, Heart, User, UserPlus, LayoutDashboard, LogOut,
  Receipt, Truck, ShieldCheck, Sparkles, Building2, Package, UploadCloud,
  ChevronDown, ChevronRight, Layers, Tag, Home, Flame, Gift, Zap, Boxes, Gem, HelpCircle
} from "lucide-react";
import { FaFacebookF, FaInstagram, FaXTwitter, FaYoutube, FaLinkedinIn, FaWhatsapp, FaPinterestP } from "react-icons/fa6";

const PROMO_ICONS = { receipt: Receipt, truck: Truck, shield: ShieldCheck };

const SearchBar = ({ placeholder, onSubmit }) => {
  const [q, setQ] = useState("");
  return (
    <form className="relative w-full" onSubmit={(e) => { e.preventDefault(); onSubmit(q); }}>
      <Search className="w-4 h-4 sm:w-5 sm:h-5 absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" strokeWidth={2} />
      <input
        data-testid="navbar-search-input"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={placeholder || "Search products, categories, product codes (e.g. ZT-101)..."}
        className="w-full h-10 sm:h-11 pl-11 sm:pl-12 pr-14 rounded-full bg-stone-50 sm:bg-white border border-stone-200 sm:border-stone-300 text-xs sm:text-sm text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-[#0a2140] focus:ring-2 focus:ring-[#0a2140]/15 transition-all"
      />
      <button
        type="submit"
        aria-label="Search"
        className="absolute right-1.5 top-1/2 -translate-y-1/2 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-[#0a2140] text-white flex items-center justify-center hover:brightness-125"
      >
        <Search className="w-3.5 h-3.5 sm:w-4 sm:h-4" strokeWidth={2} />
      </button>
    </form>
  );
};

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const { count: wishlistCount } = useWishlist();
  const { cart, openCart } = useCart();
  const { mode, setMode, isRetail, isWholesale, openChoiceModal } = useShopMode();
  const navigate = useNavigate();
  const location = useLocation();

  const siteMode = settings?.site_mode || "both";
  const storeMode = settings?.store_mode || "HYBRID";
  const isWholesaleOpen =
    settings?.wholesale_enabled !== false &&
    (siteMode === "both" || siteMode === "b2b" || storeMode === "HYBRID" || storeMode === "B2B_ONLY");
  const isRetailOpen =
    settings?.retail_enabled !== false &&
    (siteMode === "both" || siteMode === "b2c" || storeMode === "HYBRID" || storeMode === "RETAIL_ONLY");
  const bothEnabled = isRetailOpen && isWholesaleOpen;

  const handleBrandClick = (e) => {
    if (bothEnabled) {
      if (e) e.preventDefault();
      if (location.pathname !== "/") {
        navigate("/");
      }
      openChoiceModal();
    } else {
      if (location.pathname !== "/") {
        navigate("/");
      }
    }
  };
  const [open, setOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(() => {
    const isAuthRoute = location.pathname.startsWith("/auth") || location.pathname.startsWith("/register") || location.pathname.startsWith("/login");
    if (isAuthRoute) return false;
    const session = getPendingAuthSession();
    const modalActive = getAuthModalActive();
    if (session?.regOtpSent || session?.step === "otp" || session?.forgotOtpSent || modalActive) {
      return true;
    }
    return false;
  });
  const [authMode, setAuthMode] = useState(() => {
    const session = getPendingAuthSession();
    const modalActive = getAuthModalActive();
    if (session?.type === "wholesale_register" || modalActive?.mode === "b2b") return "b2b";
    if (session?.type === "retail_register" || modalActive?.mode === "b2c") return "b2c";
    return "login";
  });
  const [categoriesTree, setCategoriesTree] = useState([]);
  const [activeHoverCat, setActiveHoverCat] = useState(null);
  const [expandedDrawerCats, setExpandedDrawerCats] = useState({});
  const [isScrolled, setIsScrolled] = useState(false);
  const headerRef = useRef(null);

  useEffect(() => {
    const onVisibilityChange = () => {
      const isAuthRoute = location.pathname.startsWith("/auth") || location.pathname.startsWith("/register") || location.pathname.startsWith("/login");
      if (document.visibilityState === "visible" && !user && !isAuthRoute) {
        const session = getPendingAuthSession();
        if (session && (session.regOtpSent || session.step === "otp" || session.forgotOtpSent)) {
          setAuthMode(session.type === "wholesale_register" ? "b2b" : "b2c");
          setAuthOpen(true);
        }
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [user, location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const channel = isWholesale ? "b2b" : "b2c";
    api.get(`/categories/tree?target_channel=${channel}`)
      .then((res) => {
        setCategoriesTree(res.data || []);
      })
      .catch(() => { });
  }, [isWholesale]);

  const toggleDrawerCat = (catId) => {
    setExpandedDrawerCats(prev => ({
      ...prev,
      [catId]: !prev[catId]
    }));
  };

  useEffect(() => {
    let rafId = null;
    const updateHeight = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(() => {
        if (headerRef.current) {
          const height = headerRef.current.offsetHeight;
          document.documentElement.style.setProperty('--navbar-height', `${height}px`);
          document.documentElement.style.setProperty('--navbar-height-mobile', `${height}px`);
          document.documentElement.style.setProperty('--navbar-height-desktop', `${height}px`);
        }
      });
    };

    updateHeight();

    let ro;
    if (typeof ResizeObserver !== "undefined" && headerRef.current) {
      ro = new ResizeObserver(() => {
        updateHeight();
      });
      ro.observe(headerRef.current);
    }
    window.addEventListener("resize", updateHeight);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      if (ro) ro.disconnect();
      window.removeEventListener("resize", updateHeight);
    };
  }, [settings, mode]);

  const s = settings || {};
  const theme = s.festival_theme || "none";
  const T = s.texts || {};
  const socials = s.socials || {};

  const brandSubtitle = (() => {
    if (s.brand_tagline && s.brand_tagline.trim()) {
      const clean = s.brand_tagline.trim();
      if (/wholesale/i.test(clean)) {
        return isWholesale ? clean : clean.replace(/wholesale/gi, "RETAIL");
      }
      if (/retail/i.test(clean)) {
        return isWholesale ? clean.replace(/retail/gi, "WHOLESALE") : clean;
      }
      return `${isWholesale ? "WHOLESALE" : "RETAIL"} · ${clean}`;
    }
    return isWholesale ? "WHOLESALE · DIRECT FACTORY" : "RETAIL · COMPLETE LOOK";
  })();
  const promo = s.top_promo || [
    { icon: "shield", text: "Direct from Manufacturer · 100% Quality Hallmark" },
    { icon: "truck", text: "Fast Maharashtra & Pan-India Dispatch via Shiprocket" },
    { icon: "receipt", text: "GST Billing & COD Support Available" }
  ];
  const hasDashboard = Boolean(user && (user.role === "admin" || user.role === "seller" || user.role === "staff"));

  const openAuth = (m) => { setAuthMode(m); setAuthOpen(true); };
  const doSearch = (q) => { navigate(`/products${q ? `?search=${encodeURIComponent(q)}` : ""}`); setOpen(false); };
  const wishlistClick = () => { if (!user) { openAuth("login"); return; } navigate("/wishlist"); };
  const cartClick = () => { openCart(); };
  const loginClick = () => {
    if (user) {
      if (hasDashboard) navigate("/dashboard");
      else navigate("/dashboard");
    } else openAuth("login");
  };

  const status = s.social_status || {};
  const topSocials = [
    { icon: FaFacebookF, label: "Facebook", id: "facebook", href: socials.facebook, color: "text-[#1877F2]" },
    { icon: FaInstagram, label: "Instagram", id: "instagram", href: socials.instagram, color: "text-[#E1306C]" },
    { icon: FaXTwitter, label: "X", id: "twitter", href: socials.twitter, color: "text-[#000000]" },
    { icon: FaYoutube, label: "YouTube", id: "youtube", href: socials.youtube, color: "text-[#FF0000]" },
    { icon: FaLinkedinIn, label: "LinkedIn", id: "linkedin", href: socials.linkedin, color: "text-[#0A66C2]" },
    { icon: FaPinterestP, label: "Pinterest", id: "pinterest", href: socials.pinterest, color: "text-[#BD081C]" },
    { icon: FaWhatsapp, label: "WhatsApp", id: "whatsapp", href: socials.whatsapp, color: "text-[#25D366]" },
  ].filter((x) => x.href && status[x.id] !== false);

  return (
    <header ref={headerRef} className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm" data-testid="site-navbar">
      {/* Custom Announcement Bar from Site Settings */}
      {s.announcement_bar?.enabled && s.announcement_bar?.text && (
        <div
          className={`overflow-hidden transition-all duration-300 ease-in-out px-4 text-center text-xs font-semibold flex items-center justify-center gap-3 ${isScrolled ? "max-h-0 opacity-0 !py-0 pointer-events-none" : "max-h-16 opacity-100 py-1.5"
            }`}
          style={{
            backgroundColor: s.announcement_bar.bg_color || "#0a2140",
            color: s.announcement_bar.text_color || "#ffffff"
          }}
          data-testid="custom-announcement-bar"
        >
          {s.announcement_bar.link || s.announcement_bar.link_url ? (
            <Link
              to={s.announcement_bar.link || s.announcement_bar.link_url}
              className="hover:underline inline-block"
            >
              {s.announcement_bar.text}
            </Link>
          ) : (
            <span>{s.announcement_bar.text}</span>
          )}
          {s.announcement_bar.link_text && (
            <Link
              to={s.announcement_bar.link || s.announcement_bar.link_url || "/products"}
              className="underline hover:opacity-80 font-bold ml-1"
            >
              {s.announcement_bar.link_text}
            </Link>
          )}
        </div>
      )}

      {/* Top promo strip */}
      {promo.length > 0 && (
        <div
          className={`bg-[#faf7ef] overflow-hidden transition-all duration-300 ease-in-out flex items-center ${isScrolled
            ? "max-h-0 opacity-0 !border-b-0 pointer-events-none"
            : "max-h-10 opacity-100 border-b border-stone-200 h-8 sm:h-9"
            }`}
          data-testid="top-promo-strip"
        >
          <style>{`
            @keyframes marquee {
              0% { transform: translateX(0%); }
              100% { transform: translateX(-50%); }
            }
            .animate-marquee-custom {
              animation: marquee 22s linear infinite;
            }
            .animate-marquee-custom:hover {
              animation-play-state: paused;
            }
          `}</style>
          <div className="w-full relative flex overflow-hidden">
            <div className="flex gap-12 sm:gap-16 items-center whitespace-nowrap animate-marquee-custom px-4 shrink-0">
              {[...promo, ...promo, ...promo, ...promo].map((p, i) => {
                const Icon = PROMO_ICONS[p.icon] || Receipt;
                return (
                  <div key={i} data-testid={`promo-item-${i}`} className="flex items-center gap-1.5 text-[10px] sm:text-[11px] text-stone-700 uppercase tracking-wider font-semibold">
                    <Icon className="w-3.5 h-3.5 text-[#C79A2E]" strokeWidth={2} />
                    <span>{p.text}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Main header */}
      <div className="bg-white border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-2 sm:px-6 h-16 sm:h-20 grid grid-cols-[1fr_auto_1fr] items-center gap-1 sm:gap-4">
          {/* Left Action (Menu) */}
          <div className="flex items-center justify-start shrink-0 min-w-0">
            <button
              data-testid="navbar-menu-btn"
              onClick={() => setOpen(!open)}
              className="text-[#0a2140] hover:text-[#C79A2E] transition-colors p-1.5 rounded-lg hover:bg-stone-50 cursor-pointer shrink-0"
              aria-label="Toggle Menu"
            >
              {open ? <X className="w-5 h-5 sm:w-6 sm:h-6" /> : <Menu className="w-5 h-5 sm:w-6 sm:h-6" />}
            </button>
          </div>

          {/* Center Brand Name & Logo */}
          <div className="flex items-center justify-center gap-1.5 xs:gap-2 sm:gap-3 min-w-0 px-0.5 sm:px-2">
            <Link to="/" onClick={handleBrandClick} data-testid="nav-logo-icon" className="shrink-0 flex items-center justify-center group" aria-label="Home">
              <BrandLogo size={s.logo?.size ? Math.min(s.logo.size, 32) : 26} className="shrink-0 sm:hidden transition-transform duration-300 group-hover:scale-105" />
              <BrandLogo size={s.logo?.size || 38} className="shrink-0 hidden sm:inline-flex transition-transform duration-300 group-hover:scale-105" />
            </Link>
            <div className="flex flex-col items-center justify-center min-w-0 text-center">
              <Link to="/" onClick={handleBrandClick} data-testid="nav-logo" className="flex items-center justify-center max-w-full group">
                <span className="brand-title-text text-[17px] xs:text-[19px] sm:text-2xl lg:text-[28px] font-bold leading-none whitespace-nowrap text-center transition-all inline-flex items-center">
                  {(s.brand_name || "Zaveri Trunk").toUpperCase()}
                  <BrandTmBadge size="sm" variant="amber" />
                </span>
              </Link>
              {bothEnabled ? (
                <button
                  type="button"
                  onClick={handleBrandClick}
                  className="brand-subtitle-text text-[7px] xs:text-[7.5px] sm:text-[9.5px] hover:text-[#996515] mt-0.5 sm:mt-1 transition-colors cursor-pointer whitespace-nowrap text-center flex items-center justify-center"
                  title="Click to choose shopping experience"
                >
                  {brandSubtitle}
                </button>
              ) : (
                <span className="brand-subtitle-text text-[7px] xs:text-[7.5px] sm:text-[9.5px] text-stone-500 mt-0.5 sm:mt-1 whitespace-nowrap text-center flex items-center justify-center">
                  {brandSubtitle}
                </span>
              )}
            </div>
          </div>

          {/* Right Action Icons */}
          <div className="flex items-center justify-end gap-1 xs:gap-1.5 sm:gap-4 md:gap-5 shrink-0 min-w-0">
            {/* Wishlist - Hidden on mobile topbar, accessible via side drawer */}
            <button
              data-testid="nav-wishlist-btn"
              onClick={wishlistClick}
              className="hidden sm:flex group relative text-[#0a2140] hover:text-[#C79A2E] transition-colors flex-col items-center justify-center p-1 sm:p-1.5 cursor-pointer shrink-0"
              title="Wishlist"
            >
              <div className="relative flex items-center justify-center">
                <Heart className="w-5 h-5 sm:w-6 sm:h-6 text-[#0a2140] group-hover:text-[#C79A2E] transition-transform group-hover:scale-110" strokeWidth={1.75} />
                {user && wishlistCount > 0 && (
                  <span
                    data-testid="nav-wishlist-count"
                    className="absolute -top-1.5 -right-2 min-w-[15px] sm:min-w-[17px] h-[15px] sm:h-[17px] px-0.5 sm:px-1 rounded-full bg-[#B8860B] text-white text-[8px] sm:text-[9px] font-bold flex items-center justify-center shadow-xs"
                  >
                    {wishlistCount}
                  </span>
                )}
              </div>
              <span className="text-[9px] sm:text-[11px] font-medium text-stone-700 group-hover:text-[#0a2140] mt-0.5 sm:mt-1 tracking-tight">
                Wishlist
              </span>
            </button>

            {/* Shopping Cart */}
            <button
              data-testid="nav-cart-btn"
              onClick={cartClick}
              className="group relative text-[#0a2140] hover:text-[#C79A2E] transition-colors flex flex-col items-center justify-center p-1 sm:p-1.5 cursor-pointer shrink-0"
              title="Cart"
            >
              <div className="relative flex items-center justify-center">
                <ShoppingBag className="w-4.5 h-4.5 sm:w-6 sm:h-6 text-[#0a2140] group-hover:text-[#C79A2E] transition-transform group-hover:scale-110" strokeWidth={1.75} />
                {cart?.count > 0 && (
                  <span
                    data-testid="nav-cart-count"
                    className="absolute -top-1.5 -right-2 min-w-[15px] sm:min-w-[17px] h-[15px] sm:h-[17px] px-0.5 sm:px-1 rounded-full bg-[#C79A2E] text-white text-[8px] sm:text-[9px] font-bold flex items-center justify-center shadow-xs"
                  >
                    {cart.count}
                  </span>
                )}
              </div>
              <span className="hidden sm:block text-[9px] sm:text-[11px] font-medium text-stone-700 group-hover:text-[#0a2140] mt-0.5 sm:mt-1 tracking-tight">
                Cart
              </span>
            </button>

            {/* User Account / Login */}
            {user ? (
              <div className="flex items-center gap-0.5 sm:gap-2 shrink-0">
                <button
                  type="button"
                  data-testid="nav-home-btn"
                  onClick={() => navigate(isWholesale ? "/wholesale" : "/")}
                  className="group relative text-[#0a2140] hover:text-[#C79A2E] transition-colors flex flex-col items-center justify-center p-1 sm:p-1.5 cursor-pointer shrink-0"
                  title="Homepage"
                >
                  <div className="relative flex items-center justify-center">
                    <Home className="w-4.5 h-4.5 sm:w-6 sm:h-6 text-[#0a2140] group-hover:text-[#C79A2E] transition-transform group-hover:scale-110" strokeWidth={1.75} />
                  </div>
                  <span className="hidden sm:block text-[9px] sm:text-[11px] font-medium text-stone-700 group-hover:text-[#0a2140] mt-0.5 sm:mt-1 tracking-tight">
                    Home
                  </span>
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-1 sm:gap-3 shrink-0">
                <button
                  data-testid="nav-login-btn"
                  onClick={loginClick}
                  className="group relative text-[#0a2140] hover:text-[#C79A2E] transition-colors flex flex-col items-center justify-center p-1 sm:p-1.5 cursor-pointer"
                  title="Login"
                >
                  <div className="relative flex items-center justify-center">
                    <User className="w-4.5 h-4.5 sm:w-6 sm:h-6 text-[#0a2140] group-hover:text-[#C79A2E] transition-transform group-hover:scale-110" strokeWidth={1.75} />
                  </div>
                  <span className="hidden sm:block text-[10px] sm:text-[11px] font-medium text-stone-700 group-hover:text-[#0a2140] mt-1 tracking-tight">
                    Login
                  </span>
                </button>

                <button
                  data-testid="nav-signup-btn"
                  onClick={() => openAuth(isWholesale ? "b2b" : "b2c")}
                  className="hidden sm:flex group relative text-[#0a2140] hover:text-[#C79A2E] transition-colors flex-col items-center justify-center p-1 cursor-pointer"
                  title={isWholesale ? "B2B Sign Up" : "Sign Up"}
                >
                  <div className="relative flex items-center justify-center">
                    <UserPlus className="w-5 h-5 sm:w-6 sm:h-6 text-[#0a2140] group-hover:text-[#C79A2E] transition-transform group-hover:scale-110" strokeWidth={1.75} />
                  </div>
                  <span className="text-[10px] sm:text-[11px] font-medium text-stone-700 group-hover:text-[#0a2140] mt-1 tracking-tight">
                    Sign Up
                  </span>
                </button>

                <button
                  type="button"
                  data-testid="nav-home-btn-logged-out"
                  onClick={() => navigate(isWholesale ? "/wholesale" : "/")}
                  className="group relative text-[#0a2140] hover:text-[#C79A2E] transition-colors flex flex-col items-center justify-center p-1 sm:p-1.5 cursor-pointer shrink-0"
                  title="Homepage"
                >
                  <div className="relative flex items-center justify-center">
                    <Home className="w-4.5 h-4.5 sm:w-6 sm:h-6 text-[#0a2140] group-hover:text-[#C79A2E] transition-transform group-hover:scale-110" strokeWidth={1.75} />
                  </div>
                  <span className="hidden sm:block text-[9px] sm:text-[11px] font-medium text-stone-700 group-hover:text-[#0a2140] mt-0.5 sm:mt-1 tracking-tight">
                    Home
                  </span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Search row with Product Code hints */}
        <div className="bg-white px-3 sm:px-6 pb-2 pt-1">
          <div className="max-w-7xl mx-auto">
            <SearchBar
              placeholder={isWholesale ? "Search wholesale catalogue or Product Code (e.g. ZT-BNG-101)..." : "Search retail combos, bridal looks, necklaces..."}
              onSubmit={doSearch}
            />
          </div>
        </div>

        {/* Desktop 3-Level Category Hierarchy Navigation Strip & Mega Dropdown */}
        {categoriesTree.length > 0 && (
          <div className="hidden lg:block border-t border-stone-100 bg-[#faf8f5]/95 backdrop-blur-xs px-4 py-1 relative z-40 overflow-visible" data-testid="navbar-categories-bar">
            <div className="max-w-7xl mx-auto flex items-center justify-between gap-1 text-xs font-semibold text-stone-700 overflow-visible">
              <Link
                to={isWholesale ? "/wholesale" : "/products"}
                data-testid="navbar-cat-all"
                className="px-2.5 py-1.5 rounded-lg hover:text-[#0a2140] hover:bg-stone-100 transition-colors flex items-center gap-1.5 shrink-0"
              >
                <Tag className="w-3.5 h-3.5 text-[#B8860B]" />
                <span>{isWholesale ? "All Categories" : "All Collections"}</span>
              </Link>

              <div className="flex items-center gap-1 flex-1 justify-center overflow-visible py-0.5">
                {categoriesTree.slice(0, 8).map((cat) => (
                  <div
                    key={cat.id}
                    className="relative group/cat overflow-visible"
                    onMouseEnter={() => setActiveHoverCat(cat.id)}
                    onMouseLeave={() => setActiveHoverCat(null)}
                  >
                    <Link
                      to={isWholesale ? `/wholesale/category/${cat.id}` : `/products?category=${cat.id}`}
                      data-testid={`navbar-cat-${cat.id}`}
                      className={`px-2.5 py-1.5 rounded-lg transition-all flex items-center gap-1 hover:text-[#B8860B] hover:bg-white shadow-2xs ${activeHoverCat === cat.id ? "text-[#B8860B] bg-white font-bold" : "text-stone-700"
                        }`}
                    >
                      <span>{cat.name}</span>
                      {cat.sub_categories?.length > 0 && (
                        <ChevronDown className={`w-3 h-3 text-stone-400 transition-transform duration-200 ${activeHoverCat === cat.id ? "rotate-180 text-[#B8860B]" : "group-hover/cat:text-[#B8860B]"
                          }`} />
                      )}
                    </Link>

                    {/* Subcategories Dropdown Flyout with Hover Bridge and Top Shadow */}
                    {cat.sub_categories?.length > 0 && activeHoverCat === cat.id && (
                      <div
                        data-testid={`navbar-dropdown-${cat.id}`}
                        className="absolute top-full left-1/2 -translate-x-1/2 pt-1.5 w-64 z-[100] animate-in fade-in slide-in-from-top-1 duration-150"
                      >
                        {/* Hover Bridge (prevents mouse leave across gaps) */}
                        <div className="bg-white rounded-2xl shadow-2xl border border-stone-200/90 p-3 relative">
                          <div className="pb-1.5 mb-1.5 border-b border-stone-100 flex items-center justify-between">
                            <span className="text-[10px] font-bold text-stone-400 uppercase tracking-wider">{cat.name}</span>
                            <Link
                              to={isWholesale ? `/wholesale/category/${cat.id}` : `/products?category=${cat.id}`}
                              className="text-[10px] font-bold text-[#B8860B] hover:underline"
                            >
                              View All &rarr;
                            </Link>
                          </div>
                          <div className="grid grid-cols-1 gap-1 max-h-64 overflow-y-auto">
                            {cat.sub_categories.map((sub) => (
                              <Link
                                key={sub.id}
                                to={isWholesale ? `/wholesale/category/${cat.id}?sub_category=${sub.id}` : `/products?category=${cat.id}&sub_category=${sub.id}`}
                                data-testid={`navbar-subcat-${sub.id}`}
                                className="px-2.5 py-1.5 rounded-lg hover:bg-amber-50 hover:text-[#B8860B] transition-colors text-xs font-medium text-stone-700 flex items-center justify-between group/sub"
                              >
                                <span>{sub.name}</span>
                                <ChevronRight className="w-3 h-3 text-stone-300 group-hover/sub:text-[#B8860B] group-hover/sub:translate-x-0.5 transition-all" />
                              </Link>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

            </div>
          </div>
        )}
      </div>

      {/* Slide-in drawer menu (Slide 7: Retail Menu vs Slide 9: Wholesale Menu) */}
      {open && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setOpen(false)} />
          <nav
            data-testid="navbar-drawer"
            className="fixed left-0 top-0 bottom-0 w-[85vw] max-w-xs sm:w-80 bg-white z-50 p-6 flex flex-col gap-2 overflow-y-auto shadow-2xl"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <BrandLogo size={s.logo?.size ? Math.min(s.logo.size, 34) : 30} />
                <div className="flex flex-col items-center justify-center text-center min-w-0">
                  <span className="brand-title-text text-base sm:text-lg leading-none text-center inline-flex items-center">
                    {(s.brand_name || "Zaveri Trunk").toUpperCase()}
                    <BrandTmBadge size="xs" variant="amber" />
                  </span>
                  <span className="brand-subtitle-text text-[8px] sm:text-[9px] text-stone-500 mt-1 flex items-center justify-center">
                    {brandSubtitle}
                  </span>
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="text-stone-400 hover:text-[#C79A2E] p-1">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Dashboard / Account - Top of Hamburger Drawer */}
            <div className="mb-2">
              {user ? (
                <button
                  type="button"
                  data-testid="drawer-dashboard-btn"
                  onClick={() => {
                    setOpen(false);
                    navigate("/dashboard");
                  }}
                  className="w-full text-left p-3 rounded-2xl bg-gradient-to-r from-[#0a2140] via-[#102a4e] to-[#0a2140] text-white shadow-md hover:shadow-lg border border-[#C79A2E]/40 hover:border-[#C79A2E] transition-all group cursor-pointer"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-xl bg-[#C79A2E]/20 border border-[#C79A2E]/40 flex items-center justify-center shrink-0 text-[#C79A2E] group-hover:scale-105 transition-transform">
                        {hasDashboard ? (
                          <LayoutDashboard className="w-5 h-5 text-[#C79A2E]" strokeWidth={2} />
                        ) : (
                          <User className="w-5 h-5 text-[#C79A2E]" strokeWidth={2} />
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-white tracking-wide">
                            {user.role === "admin" ? "Admin Dashboard" : user.role === "seller" ? "Seller Dashboard" : "My Dashboard"}
                          </span>
                          <span className="text-[9px] uppercase px-1.5 py-0.5 rounded-full font-bold bg-[#C79A2E] text-white">
                            {user.role === "admin" ? "Admin" : user.role === "seller" ? "Seller" : "Account"}
                          </span>
                        </div>
                        <p className="text-[10px] text-amber-200/80 truncate mt-0.5">
                          {user.name || user.email || "Manage store & account"}
                        </p>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-amber-300 group-hover:translate-x-0.5 transition-transform shrink-0" />
                  </div>
                </button>
              ) : (
                <button
                  type="button"
                  data-testid="drawer-dashboard-btn"
                  onClick={() => {
                    setOpen(false);
                    openAuth("login");
                  }}
                  className="w-full text-left p-3 rounded-2xl bg-gradient-to-r from-[#0a2140] via-[#102a4e] to-[#0a2140] text-white shadow-md hover:shadow-lg border border-[#C79A2E]/40 hover:border-[#C79A2E] transition-all group cursor-pointer"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-xl bg-[#C79A2E]/20 border border-[#C79A2E]/40 flex items-center justify-center shrink-0 text-[#C79A2E] group-hover:scale-105 transition-transform">
                        <LayoutDashboard className="w-5 h-5 text-[#C79A2E]" strokeWidth={2} />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-white tracking-wide">
                            Dashboard
                          </span>
                        </div>
                        <p className="text-[10px] text-amber-200/80 truncate mt-0.5">
                          Login to access Dashboard
                        </p>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-amber-300 group-hover:translate-x-0.5 transition-transform shrink-0" />
                  </div>
                </button>
              )}
            </div>

            {/* Mobile Drawer Store Info */}
            <div className="bg-stone-50 p-2.5 rounded-2xl border border-stone-200 mb-2">
              <div className="flex items-center justify-between px-1">
                <div>
                  <p className="text-[10px] font-bold text-stone-400 uppercase tracking-widest">Active Channel</p>
                  <p className="text-xs font-bold text-stone-900 capitalize flex items-center gap-1.5 mt-0.5">
                    {isWholesale ? (
                      <span className="text-[#996515] flex items-center gap-1 font-bold">
                        <Building2 className="w-3.5 h-3.5" /> B2B Wholesale
                      </span>
                    ) : (
                      <span className="text-[#0a2140] flex items-center gap-1 font-bold">
                        <ShoppingBag className="w-3.5 h-3.5" /> B2C Retail
                      </span>
                    )}
                  </p>
                </div>
                {bothEnabled ? (
                  <button
                    type="button"
                    onClick={() => { setOpen(false); openChoiceModal(); }}
                    className="text-[10px] font-bold text-[#B8860B] bg-amber-50 hover:bg-amber-100 border border-amber-200 px-2 py-1 rounded-lg transition-colors cursor-pointer"
                  >
                    Switch
                  </button>
                ) : (
                  <span className="text-[10px] font-semibold text-stone-500 bg-stone-200/70 px-2 py-0.5 rounded-md">
                    Active
                  </span>
                )}
              </div>
            </div>

            {/* DYNAMIC MENU ITEMS */}
            {isRetail ? (
              /* RETAIL MENU */
              <div className="space-y-1">
                <p className="text-[10px] font-bold text-stone-400 uppercase tracking-widest px-2 mb-1">Retail Journey</p>
                <Link to="/" onClick={() => setOpen(false)} className="px-3 py-2 text-sm font-semibold text-stone-800 hover:text-[#0a2140] hover:bg-stone-50 rounded-xl flex items-center gap-2.5">
                  <Home className="w-4 h-4 text-[#C79A2E]" />
                  <span>Home</span>
                </Link>
                <Link to="/products" onClick={() => setOpen(false)} className="px-3 py-2 text-sm font-semibold text-stone-800 hover:text-[#0a2140] hover:bg-stone-50 rounded-xl flex items-center gap-2.5">
                  <Gem className="w-4 h-4 text-purple-600" />
                  <span>Retail Collection</span>
                </Link>
                <a href="/#trending-combos" onClick={() => setOpen(false)} className="px-3 py-2 text-sm font-semibold text-stone-800 hover:text-[#0a2140] hover:bg-stone-50 rounded-xl flex items-center justify-between">
                  <span className="flex items-center gap-2.5">
                    <Flame className="w-4 h-4 text-amber-500 fill-amber-500/20" />
                    <span>Trending Combos</span>
                  </span>
                  <span className="text-[10px] bg-amber-100 text-[#996515] font-bold px-2 py-0.5 rounded-full">Popular</span>
                </a>
                <a href="/#festive-special" onClick={() => setOpen(false)} className="px-3 py-2 text-sm font-semibold text-stone-800 hover:text-[#0a2140] hover:bg-stone-50 rounded-xl flex items-center gap-2.5">
                  <Gift className="w-4 h-4 text-rose-500" />
                  <span>Festive Special</span>
                </a>
                <a href="/#new-arrivals" onClick={() => setOpen(false)} className="px-3 py-2 text-sm font-semibold text-stone-800 hover:text-[#0a2140] hover:bg-stone-50 rounded-xl flex items-center gap-2.5">
                  <Zap className="w-4 h-4 text-emerald-600 fill-emerald-600/20" />
                  <span>New Arrivals</span>
                </a>
                <Link to="/wishlist" data-testid="drawer-wishlist-btn" onClick={() => setOpen(false)} className="px-3 py-2 text-sm font-semibold text-stone-800 hover:text-[#0a2140] hover:bg-stone-50 rounded-xl flex items-center justify-between">
                  <span className="flex items-center gap-2.5">
                    <Heart className="w-4 h-4 text-red-500 fill-red-500/20" />
                    <span>My Wishlist</span>
                  </span>
                  {wishlistCount > 0 && (
                    <span className="text-[10px] bg-red-100 text-red-700 font-bold px-2 py-0.5 rounded-full">{wishlistCount} items</span>
                  )}
                </Link>
                <Link to="/orders" onClick={() => setOpen(false)} className="px-3 py-2 text-sm font-semibold text-stone-800 hover:text-[#0a2140] hover:bg-stone-50 rounded-xl flex items-center justify-between">
                  <span className="flex items-center gap-2.5">
                    <Truck className="w-4 h-4 text-blue-600" />
                    <span>My Orders & Track</span>
                  </span>
                </Link>
                <Link to="/faqs" onClick={() => setOpen(false)} className="px-3 py-2 text-sm font-semibold text-stone-800 hover:text-[#0a2140] hover:bg-stone-50 rounded-xl flex items-center gap-2.5">
                  <HelpCircle className="w-4 h-4 text-[#B8860B]" />
                  <span>FAQs & Help</span>
                </Link>
              </div>
            ) : (
              /* WHOLESALE MENU */
              <div className="space-y-1">
                <p className="text-[10px] font-bold text-stone-400 uppercase tracking-widest px-2 mb-1">Wholesale Journey</p>
                <Link to="/wholesale" onClick={() => setOpen(false)} className="px-3 py-2 text-sm font-semibold text-stone-800 hover:text-[#0a2140] hover:bg-stone-50 rounded-xl flex items-center gap-2.5">
                  <Home className="w-4 h-4 text-[#C79A2E]" />
                  <span>Wholesale Home</span>
                </Link>
                <Link to="/products" onClick={() => setOpen(false)} className="px-3 py-2 text-sm font-semibold text-stone-800 hover:text-[#0a2140] hover:bg-stone-50 rounded-xl flex items-center gap-2.5">
                  <Gem className="w-4 h-4 text-[#0a2140]" />
                  <span>B2B Catalogue</span>
                </Link>
                <Link to="/custom-request" onClick={() => setOpen(false)} className="px-3 py-2 text-sm font-semibold text-stone-800 hover:text-[#0a2140] hover:bg-stone-50 rounded-xl flex items-center gap-2.5">
                  <Sparkles className="w-4 h-4 text-purple-600" />
                  <span>Custom Design Request</span>
                </Link>
                <Link to="/wishlist" data-testid="drawer-wholesale-wishlist-btn" onClick={() => setOpen(false)} className="px-3 py-2 text-sm font-semibold text-stone-800 hover:text-[#0a2140] hover:bg-stone-50 rounded-xl flex items-center justify-between">
                  <span className="flex items-center gap-2.5">
                    <Heart className="w-4 h-4 text-red-500 fill-red-500/20" />
                    <span>My Wishlist</span>
                  </span>
                  {wishlistCount > 0 && (
                    <span className="text-[10px] bg-red-100 text-red-700 font-bold px-2 py-0.5 rounded-full">{wishlistCount} items</span>
                  )}
                </Link>
                <Link to="/orders" onClick={() => setOpen(false)} className="px-3 py-2 text-sm font-semibold text-stone-800 hover:text-[#0a2140] hover:bg-stone-50 rounded-xl flex items-center justify-between">
                  <span className="flex items-center gap-2.5">
                    <Truck className="w-4 h-4 text-blue-600" />
                    <span>My Orders & Track</span>
                  </span>
                </Link>
                <Link to="/faqs" onClick={() => setOpen(false)} className="px-3 py-2 text-sm font-semibold text-stone-800 hover:text-[#0a2140] hover:bg-stone-50 rounded-xl flex items-center gap-2.5">
                  <HelpCircle className="w-4 h-4 text-[#B8860B]" />
                  <span>FAQs & Wholesale Help</span>
                </Link>
              </div>
            )}
            {categoriesTree.length > 0 && (
              <div className="space-y-1 my-2" data-testid="drawer-categories-accordion">
                <p className="text-[10px] font-bold text-stone-400 uppercase tracking-widest px-2 mb-1">
                  Explore Categories
                </p>
                {categoriesTree.map((cat) => {
                  const isExpanded = Boolean(expandedDrawerCats[cat.id]);
                  const hasSubs = cat.sub_categories?.length > 0;
                  return (
                    <div key={cat.id} className="rounded-xl overflow-hidden border border-stone-100 bg-stone-50/70">
                      <div className="flex items-center justify-between px-3 py-2">
                        <Link
                          to={isWholesale ? `/wholesale/category/${cat.id}` : `/products?category=${cat.id}`}
                          onClick={() => setOpen(false)}
                          className="text-xs font-semibold text-stone-800 hover:text-[#B8860B] flex-1 truncate"
                        >
                          {cat.name}
                        </Link>
                        {hasSubs && (
                          <button
                            type="button"
                            onClick={() => toggleDrawerCat(cat.id)}
                            aria-label={`Toggle ${cat.name} subcategories`}
                            className="p-1 text-stone-400 hover:text-stone-700 rounded-md transition-colors"
                          >
                            <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isExpanded ? "rotate-180 text-[#B8860B]" : ""}`} />
                          </button>
                        )}
                      </div>
                      {hasSubs && isExpanded && (
                        <div className="bg-white px-3 py-1.5 border-t border-stone-100 space-y-1 animate-in fade-in duration-150">
                          <Link
                            to={isWholesale ? `/wholesale/category/${cat.id}` : `/products?category=${cat.id}`}
                            onClick={() => setOpen(false)}
                            className="block px-2 py-1 text-[11px] font-bold text-[#B8860B] hover:bg-amber-50 rounded-lg"
                          >
                            All {cat.name}
                          </Link>
                          {cat.sub_categories.map((sub) => (
                            <Link
                              key={sub.id}
                              to={isWholesale ? `/wholesale/category/${cat.id}?sub_category=${sub.id}` : `/products?category=${cat.id}&sub_category=${sub.id}`}
                              onClick={() => setOpen(false)}
                              className="block px-2 py-1 text-[11px] font-medium text-stone-600 hover:text-[#B8860B] hover:bg-amber-50 rounded-lg truncate"
                            >
                              {sub.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* User Account / Login */}
            <div className="border-t border-stone-100 pt-3 mt-2 space-y-1">
              {user ? (
                <>
                  <button
                    type="button"
                    onClick={async () => { setOpen(false); await logout(); navigate("/"); }}
                    className="w-full text-left px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 rounded-xl flex items-center gap-2 cursor-pointer"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Logout</span>
                  </button>
                </>
              ) : (
                <div className="space-y-1">
                  <p className="text-[10px] font-bold text-stone-400 uppercase tracking-widest px-3 mb-1">Account & Access</p>
                  <button
                    type="button"
                    data-testid="drawer-login-btn"
                    onClick={() => { setOpen(false); openAuth("login"); }}
                    className="w-full text-left px-3 py-2 text-sm font-semibold text-stone-800 hover:text-[#0a2140] hover:bg-stone-50 rounded-xl flex items-center gap-2 cursor-pointer"
                  >
                    <User className="w-4 h-4 text-[#0a2140]" />
                    <span>Login to Account</span>
                  </button>

                  <button
                    type="button"
                    data-testid="drawer-b2c-signup-btn"
                    onClick={() => { setOpen(false); openAuth("b2c"); }}
                    className="w-full text-left px-3 py-2 text-sm font-semibold text-stone-800 hover:text-[#0a2140] hover:bg-stone-50 rounded-xl flex items-center justify-between cursor-pointer"
                  >
                    <span className="flex items-center gap-2">
                      <UserPlus className="w-4 h-4 text-emerald-600" />
                      <span>B2C Customer Sign Up</span>
                    </span>
                    <span className="text-[10px] bg-emerald-100 text-emerald-800 font-bold px-2 py-0.5 rounded-full">Retail</span>
                  </button>

                  <button
                    type="button"
                    data-testid="drawer-b2b-signup-btn"
                    onClick={() => { setOpen(false); openAuth("b2b"); }}
                    className="w-full text-left px-3 py-2 text-sm font-semibold text-amber-900 bg-amber-50/70 hover:bg-amber-100 rounded-xl flex items-center justify-between cursor-pointer border border-amber-200/60"
                  >
                    <span className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-[#996515]" />
                      <span>B2B Wholesale Sign Up</span>
                    </span>
                    <span className="text-[10px] bg-[#996515] text-white font-bold px-2 py-0.5 rounded-full">Merchant</span>
                  </button>
                </div>
              )}
            </div>

            {/* WhatsApp Floating Desk Support */}
            <a
              href={waLink(s.support_whatsapp || socials.whatsapp || "919082444611", `Hi! I need assistance with ${s.brand_name || "Zaveri Trunk"}.`)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-whatsapp-support"
              onClick={() => setOpen(false)}
              className="mt-auto p-3 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-bold flex items-center gap-2"
            >
              <FaWhatsapp className="w-5 h-5 text-[#25D366]" />
              <div>
                <p>Direct WhatsApp Support</p>
                <p className="text-[10px] text-emerald-600 font-normal">Immediate response from manufacturer</p>
              </div>
            </a>
          </nav>
        </>
      )}

      <AuthModal open={authOpen} mode={authMode} onOpenChange={setAuthOpen} />
    </header>
  );
};
